import { Component, Input, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import {
  formatMultiBuyOfferSummary,
  formatPricingDate,
  getDiscountGroupOfferStatus,
  MultiBuyOfferStatusInfo,
} from 'src/app/services/product-pricing.util';
import { DiscountGroupOfferFormDialogComponent } from './discount-group-offer-form-dialog/discount-group-offer-form-dialog.component';

@Component({
  selector: 'app-discount-group-offers',
  templateUrl: './discount-group-offers.component.html',
  styleUrls: ['./discount-group-offers.component.scss'],
})
export class DiscountGroupOffersComponent implements OnInit {
  @Input() storeId: string = '';

  offers: any[] = [];
  groupStatsByKey: Record<string, { productsWithOwnOffers: number; productCount: number }> = {};
  loading = false;
  loadError = '';

  expandedOfferId: string | null = null;
  expandedProducts: any[] = [];
  expandedProductsLoading = false;
  expandedProductsError = '';

  constructor(
    private ecommerceService: EcommerceService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadOffers();
    }
  }

  loadOffers(): void {
    this.loading = true;
    this.loadError = '';
    this.spinner.show();

    this.ecommerceService.getDiscountGroupOffers(this.storeId).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        this.offers = this.extractItems(data);
        this.loading = false;
        this.spinner.hide();
        this.loadGroupStats();
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        this.loadError = err?.error?.response?.message || 'Failed to load discount group offers';
        this.offers = [];
      },
    });
  }

  private loadGroupStats(): void {
    this.ecommerceService.getDiscountGroupsForStore(this.storeId).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        const items = this.extractItems(data);
        const map: Record<string, { productsWithOwnOffers: number; productCount: number }> = {};
        items.forEach((g: any) => {
          const key = (g.discountGroup || '').trim().toLowerCase();
          if (!key) {
            return;
          }
          map[key] = {
            productsWithOwnOffers: g.productsWithOwnOffers ?? 0,
            productCount: g.productCount ?? 0,
          };
        });
        this.groupStatsByKey = map;
      },
      error: () => {
        this.groupStatsByKey = {};
      },
    });
  }

  openAddDialog(): void {
    this.dialogService
      .open(DiscountGroupOfferFormDialogComponent, {
        context: { storeId: this.storeId, offer: null },
        hasBackdrop: true,
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((result: any) => {
        if (result?.saved) {
          this.loadOffers();
        }
      });
  }

  openEditDialog(offer: any): void {
    this.dialogService
      .open(DiscountGroupOfferFormDialogComponent, {
        context: { storeId: this.storeId, offer },
        hasBackdrop: true,
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((result: any) => {
        if (result?.saved) {
          this.loadOffers();
        }
      });
  }

  onActiveToggle(offer: any, checked: boolean): void {
    if (!offer?._id) {
      return;
    }
    const currentlyActive = offer.isActive !== false;
    if (checked === currentlyActive) {
      return;
    }

    this.ecommerceService
      .updateDiscountGroupOffer(this.storeId, offer._id, { isActive: checked })
      .subscribe({
        next: () => {
          this.toastr.success(checked ? 'Offer enabled' : 'Offer disabled');
          this.loadOffers();
        },
        error: (err: any) => {
          this.toastr.error(err?.error?.response?.message || 'Failed to update offer');
          this.loadOffers();
        },
      });
  }

  isOfferActiveToggle(offer: any): boolean {
    return offer?.isActive !== false;
  }

  deleteOffer(offer: any): void {
    if (!offer?._id) {
      return;
    }

    Swal.fire({
      title: 'Delete group offer?',
      text: formatMultiBuyOfferSummary(offer, 'DKK'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.ecommerceService.deleteDiscountGroupOffer(this.storeId, offer._id).subscribe({
        next: () => {
          this.toastr.success('Offer deleted');
          if (this.expandedOfferId === offer._id) {
            this.expandedOfferId = null;
            this.expandedProducts = [];
          }
          this.loadOffers();
        },
        error: (err: any) => {
          Swal.fire('Error', err?.error?.response?.message || 'Failed to delete offer', 'error');
        },
      });
    });
  }

  toggleExpand(offer: any): void {
    if (!offer?._id) {
      return;
    }
    if (this.expandedOfferId === offer._id) {
      this.expandedOfferId = null;
      this.expandedProducts = [];
      this.expandedProductsError = '';
      return;
    }

    this.expandedOfferId = offer._id;
    this.expandedProducts = [];
    this.expandedProductsLoading = true;
    this.expandedProductsError = '';

    this.loadExpandedProducts(offer);
  }

  reloadExpandedProducts(offer: any): void {
    if (!this.isExpanded(offer)) {
      return;
    }
    this.loadExpandedProducts(offer);
  }

  private loadExpandedProducts(offer: any): void {
    this.expandedProductsLoading = true;
    this.expandedProductsError = '';
    this.ecommerceService.getDiscountGroupMemberProducts(this.storeId, offer.discountGroup).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        this.expandedProducts = this.extractItems(data);
        this.expandedProductsLoading = false;
      },
      error: (err: any) => {
        this.expandedProductsLoading = false;
        this.expandedProductsError =
          err?.error?.response?.message || 'Failed to load group products';
      },
    });
  }

  isExpanded(offer: any): boolean {
    return !!offer?._id && this.expandedOfferId === offer._id;
  }

  offerDisplayName(offer: any): string {
    const name = (offer?.name || '').trim();
    return name || offer?.discountGroup || '—';
  }

  offerSummary(offer: any): string {
    return formatMultiBuyOfferSummary(offer, 'DKK');
  }

  getOfferStatus(offer: any): MultiBuyOfferStatusInfo {
    return getDiscountGroupOfferStatus(offer);
  }

  formatOfferDate(value: string): string {
    return formatPricingDate(value);
  }

  formatCurrency(amount: number): string {
    if (amount == null || Number.isNaN(Number(amount))) {
      return '—';
    }
    return `${Number(amount).toFixed(2)} DKK`;
  }

  productsWithOwnOffersCount(offer: any): number {
    const key = (offer?.discountGroup || '').trim().toLowerCase();
    return this.groupStatsByKey[key]?.productsWithOwnOffers ?? 0;
  }

  groupProductCount(offer: any): number {
    const key = (offer?.discountGroup || '').trim().toLowerCase();
    return this.groupStatsByKey[key]?.productCount ?? 0;
  }

  private extractItems(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.items && Array.isArray(data.items)) {
      return data.items;
    }
    return [];
  }
}
